import {
  CircularProgress,
  HStack,
  VStack,
  Box,
  Skeleton,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { UserAvatar } from 'Organisms';
import { Typography } from 'Tokens';
import {
  DisclosureRequirementOwner_Constraint_,
  DisclosureRequirementOwner_Update_Column_,
  GetDisclosureRequirementsOwnerDocument_,
  ShortEsrsAssessmentDocument_,
  User,
  useUpsertDisclosureRequirementsOwnerMutation,
  useUpsertMaterialStandardOwnerMutation,
} from 'models';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DREnums } from 'containers/Esrs';
import { useTranslation } from 'utils/translation';
import { IconButton } from 'Atoms';
import { EditIcon } from 'Tokens/Icons/Data';
import { Menu } from 'Molecules/Menu';
import { ResponsibleSelector, ToolbarToast } from 'Molecules/TaskToolbar';
import { useHandleTaskUpdate } from '../../Metrics/Metrics/ProcessMetrics/MetricsHelperFunctions';
import { SelectedRowData } from '../../Metrics/MetricsTable/MetricsTableComponents/SelectedRows/SelectedRows';
import { EmptyOwner } from 'Atoms/EmptyOwner';
import { useNhostClient } from 'Providers/nhost/NhostProvider.tsx';

export const InfoCard = ({
  title,
  subtitleText,
  content,
}: {
  title: string;
  subtitleText?: string;
  content: ReactNode;
}) => {
  return (
    <VStack alignItems="flex-start" bg="bg.muted" flex={1} p="20px" borderRadius="8px" gap="0px">
      <Typography variant="h3">
        {title}
        {subtitleText && '*'}
      </Typography>
      <Box minHeight="23px;">
        {subtitleText && (
          <Typography fontSize="xs" color="text.muted" lineHeight="12px;">
            {subtitleText}
          </Typography>
        )}
      </Box>
      {content}
    </VStack>
  );
};
export const MetricInfoCard = ({
  title,
  content,
  onMouseEnter,
  onMouseLeave,
}: {
  title: string;
  content: ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) => {
  return (
    <VStack
      alignItems="flex-start"
      bg="bg.muted"
      flex={1}
      p="20px"
      borderRadius="8px"
      gap="8px"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Typography variant="body">{title}</Typography>
      {content}
    </VStack>
  );
};
const RegularCompanyStandardProgress = ({
  disclosureRequirementsRefs,
  parentAssessmentId,
}: {
  disclosureRequirementsRefs: string[];
  parentAssessmentId: string;
}) => {
  const nhost = useNhostClient();
  const { esrsAssessmentId, standardRef, framework = '' } = useParams();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('0');

  const getProgress = async () => {
    setLoading(true);
    const res = await nhost.functions.post<{ progress: string }>(
      '/esrs/progress/standard-progress',
      {
        drRefs: disclosureRequirementsRefs,
        assessmentId: esrsAssessmentId,
        standardRef,
        parentAssessmentId,
        type: 'company',
        frameworkRef: framework,
      }
    );

    if (res.body.progress) {
      setProgress(res.body.progress);
    }
    setLoading(false);
  };

  useEffect(() => {
    getProgress();
  }, [disclosureRequirementsRefs, esrsAssessmentId, standardRef, parentAssessmentId]);

  return (
    <Skeleton isLoaded={!loading}>
      <HStack spacing="8px">
        <CircularProgress
          size="16px"
          thickness="12px"
          color="text.info"
          value={Number(progress ?? '0')}
        />
        <Typography variant="h3">{`${progress}%`}</Typography>
      </HStack>
    </Skeleton>
  );
};

const ReportingUnitStandardProgress = ({
  disclosureRequirementsRefs,
  parentAssessmentId,
}: {
  disclosureRequirementsRefs: string[];
  parentAssessmentId: string | undefined;
}) => {
  const nhost = useNhostClient();
  const { esrsAssessmentId, standardRef, reportingUnitId, framework = '' } = useParams();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('0');

  const getProgress = async () => {
    setLoading(true);
    const res = await nhost.functions.post<{ progress: string }>(
      '/esrs/progress/standard-progress',
      {
        drRefs: disclosureRequirementsRefs,
        assessmentId: esrsAssessmentId,
        standardRef,
        parentAssessmentId,
        reportingUnitId,
        type: 'reportingUnit',
        frameworkRef: framework,
      }
    );

    if (res.body.progress) {
      setProgress(res.body.progress);
    }
    setLoading(false);
  };

  useEffect(() => {
    getProgress();
  }, [
    disclosureRequirementsRefs,
    esrsAssessmentId,
    standardRef,
    parentAssessmentId,
    reportingUnitId,
  ]);

  return (
    <Skeleton isLoaded={!loading}>
      <HStack spacing="8px">
        <CircularProgress
          size="28px"
          thickness="16px"
          color="text.info"
          value={Number(progress ?? '0')}
        />
        <Typography variant="h3">{`${progress}%`}</Typography>
      </HStack>
    </Skeleton>
  );
};

const GroupStandardProgress = ({
  disclosureRequirementsRefs,
}: {
  disclosureRequirementsRefs: string[];
}) => {
  const { esrsAssessmentId, standardRef, framework = '' } = useParams();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('0');
  const nhost = useNhostClient();

  const getProgress = async () => {
    setLoading(true);
    const res = await nhost.functions.post<{ progress: string }>(
      '/esrs/progress/standard-progress',
      {
        drRefs: disclosureRequirementsRefs,
        assessmentId: esrsAssessmentId,
        standardRef,
        type: 'group',
        frameworkRef: framework,
      }
    );

    if (res.body.progress) {
      setProgress(res.body.progress);
    }
    setLoading(false);
  };

  useEffect(() => {
    getProgress();
  }, [disclosureRequirementsRefs, esrsAssessmentId, standardRef]);

  return (
    <Skeleton isLoaded={!loading}>
      <HStack spacing="8px">
        <CircularProgress
          size="28px"
          thickness="16px"
          color="text.info"
          value={Number(progress ?? '0')}
        />
        <Typography variant="h3">{`${progress}%`}</Typography>
      </HStack>
    </Skeleton>
  );
};

export const DisclosureRequirementHeader = ({
  isGroup,
  owner,
  disclosureRequirementsRefs,
  parentAssessmentId,
  requirements,
  materialStandardId,
  isMfStandard,
  selectedRows,
  companyLevelReportingUnitId,
  allDisclosureRequirementsRefs,
  dpRowsLoading,
}: {
  isGroup: boolean;
  owner?: Partial<User> | null;
  selectedRows: SelectedRowData[];
  disclosureRequirementsRefs: string[];
  parentAssessmentId: string;
  requirements: any;
  materialStandardId?: string;
  isMfStandard: boolean;
  companyLevelReportingUnitId: string;
  allDisclosureRequirementsRefs: string[];
  dpRowsLoading: boolean;
}) => {
  const { t } = useTranslation(['common']);
  const toast = useToast();
  const { reportingUnitId = '' } = useParams();
  const url = window.location.href;
  const [isOwnerHovered, setIsOwnerHovered] = useState(false);
  const [responsibleFor, setResponsibleFor] = useState('all');
  const { isOpen, onClose, onOpen } = useDisclosure();
  const isBusinessUnit = useMemo(() => url.includes('bu-standard'), [url]);
  const constraint = isMfStandard
    ? DisclosureRequirementOwner_Constraint_.DisclosureRequirementOwnerMaterialStandardIdMfDisclosureRKey_
    : DisclosureRequirementOwner_Constraint_.DisclosureRequirementOwnerMaterialStandardIdDisclosureReqKey_;

  const [upsertStandardOwner] = useUpsertMaterialStandardOwnerMutation();
  const [updateDrOwners] = useUpsertDisclosureRequirementsOwnerMutation();
  const handleTaskUpdate = useHandleTaskUpdate(
    selectedRows,
    owner?.id,
    companyLevelReportingUnitId
  );

  const standardRefExcluded = useMemo(
    () =>
      requirements
        .flat()
        .filter((item: any) => item.type === DREnums.action || item.type === DREnums.target)
        .map((item: any) => item.reference),
    [requirements]
  );

  const subtitleText = useMemo(
    () =>
      standardRefExcluded.length
        ? t('common:standardRefExcludedText', { standards: standardRefExcluded.join(', ') })
        : '',
    [standardRefExcluded]
  );

  const handleUpdateOwner = (ownerId: string, ownerName?: string) => {
    upsertStandardOwner({
      variables: {
        materialStandardId,
        ownerId,
        reportingUnitId: isBusinessUnit ? reportingUnitId : companyLevelReportingUnitId,
      },
      refetchQueries: [ShortEsrsAssessmentDocument_],
    }).then(() => {
      if (responsibleFor !== 'all') {
        toast.close('loadingToast');
        toast({
          position: 'bottom',
          duration: 3000,
          render: () => (
            <ToolbarToast
              text={`Assigned ${ownerName} as responsible`}
              isStandalone
              visible={true}
              isLoading={false}
            />
          ),
        });
      }
    });
    if (responsibleFor === 'all') {
      updateDrOwners({
        variables: {
          objects: allDisclosureRequirementsRefs.map((ref) => ({
            ...(isMfStandard
              ? { mfDisclosureRequirementRef: ref }
              : { disclosureRequirementRef: ref }),
            materialStandardId,
            ownerId,
            reportingUnitId: isBusinessUnit ? reportingUnitId : companyLevelReportingUnitId,
          })),
          on_conflict: {
            constraint: constraint,
            update_columns: [DisclosureRequirementOwner_Update_Column_.OwnerId_],
          },
        },
        refetchQueries: [ShortEsrsAssessmentDocument_, GetDisclosureRequirementsOwnerDocument_],
      });

      handleTaskUpdate('Responsible', {
        responsible: ownerId,
      }).then(() => {
        toast.close('loadingToast');
        toast({
          position: 'bottom',
          duration: 3000,
          render: () => (
            <ToolbarToast
              text={`Assigned ${ownerName} as responsible`}
              isStandalone
              visible={true}
              isLoading={false}
            />
          ),
        });
      });
    }
  };

  return (
    <VStack alignItems="stretch">
      <Typography variant="h2">Disclosure Requirements</Typography>
      <HStack w="100%" gap="8px" align="stretch">
        <InfoCard
          title="Data collection progress"
          subtitleText={subtitleText}
          content={
            isGroup ? (
              <GroupStandardProgress disclosureRequirementsRefs={disclosureRequirementsRefs} />
            ) : isBusinessUnit ? (
              <ReportingUnitStandardProgress
                disclosureRequirementsRefs={disclosureRequirementsRefs}
                parentAssessmentId={parentAssessmentId}
              />
            ) : (
              <RegularCompanyStandardProgress
                disclosureRequirementsRefs={disclosureRequirementsRefs}
                parentAssessmentId={parentAssessmentId}
              />
            )
          }
        />
        <MetricInfoCard
          title="Main responsible"
          onMouseEnter={() => setIsOwnerHovered(true)}
          onMouseLeave={() => setIsOwnerHovered(false)}
          content={
            <HStack spacing="8px">
              {owner ? (
                <UserAvatar user={owner as User} size="sm" boxSize="28px" border="none" />
              ) : (
                <EmptyOwner />
              )}
              <Typography variant="h3">{owner ? owner?.displayName : 'Assign'}</Typography>
              {isOwnerHovered && !dpRowsLoading && (
                <Menu
                  minListWidth="280px"
                  menuButton={
                    <IconButton
                      aria-label="edit-dr-owner"
                      icon={<EditIcon />}
                      borderRadius="50%"
                      variant="ghost"
                      size="sm"
                    />
                  }
                  sections={[
                    {
                      title: 'Assign responsability to',
                      actions: [
                        {
                          id: 'all',
                          title: 'Standard and all data points',
                          onClick: () => {
                            setResponsibleFor('all');
                            onOpen();
                          },
                        },
                        {
                          id: 'all',
                          title: 'Standard only',
                          onClick: () => {
                            setResponsibleFor('standard');
                            onOpen();
                          },
                        },
                      ],
                    },
                  ]}
                  placement="bottom"
                />
              )}
            </HStack>
          }
        />
      </HStack>
      <ResponsibleSelector
        isOpen={isOpen}
        onClose={onClose}
        onSelect={handleUpdateOwner}
        isStandalone
      />
    </VStack>
  );
};
